import React, { useState, useEffect, useRef } from 'react';
import { HashLink } from 'react-router-hash-link';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProjectModal } from '../ProjectModal/ProjectModalContext';

const NavLinks = ({ mobile = false }) => {
  const { t, i18n } = useTranslation();
  const { open } = useProjectModal();
  const { pathname } = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selectedLanguage = localStorage.getItem('selectedLanguage');
    if (selectedLanguage) {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('selectedLanguage', lng);
    setIsDropdownOpen(false);
  };

  const currentLang = (i18n.language || 'en').startsWith('fr') ? 'FR' : 'EN';

  const linkClass = (to) => {
    const active = pathname === to;
    if (mobile) {
      return `px-3 py-2.5 rounded-lg text-base font-semibold transition ${active ? 'bg-brand-50 text-brand-700' : 'text-slatex-700 hover:bg-slatex-100 hover:text-brand-700'}`;
    }
    return `relative px-4 py-2 text-sm font-semibold transition ${active
      ? 'text-brand-700 after:absolute after:left-4 after:right-4 after:-bottom-1 after:h-0.5 after:rounded-full after:bg-brand-500'
      : 'text-slatex-600 hover:text-brand-700'}`;
  };

  return (
    <div className={mobile ? 'flex flex-col gap-1' : 'flex items-center gap-1'}>
      <HashLink className={linkClass('/about')} smooth to="/about">
        {t('navbar.about')}
      </HashLink>
      <HashLink className={linkClass('/services')} smooth to="/services">
        {t('navbar.services')}
      </HashLink>
      <HashLink className={linkClass('/portefolio')} to="/portefolio">
        {t('navbar.portfolio')}
      </HashLink>

      <div className={`relative ${mobile ? 'px-3 py-2' : 'ml-1'}`} ref={dropdownRef}>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-slatex-200 px-3 py-1.5 text-sm font-semibold text-slatex-600 hover:border-brand-300 hover:text-brand-700 transition focus:outline-none"
          onClick={() => setIsDropdownOpen((v) => !v)}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3c2.5 2.5 3.8 5.6 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.6-3.8-9s1.3-6.5 3.8-9z" />
          </svg>
          {currentLang}
          <svg className="w-3 h-3" viewBox="0 0 10 6" fill="none">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" d="m1 1 4 4 4-4" />
          </svg>
        </button>
        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-36 rounded-xl border border-slatex-200 bg-white shadow-card overflow-hidden z-40">
            <button
              className={`block w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-slatex-100 ${currentLang === 'EN' ? 'text-brand-700' : 'text-slatex-600'}`}
              onClick={() => changeLanguage('en')}
            >
              English
            </button>
            <button
              className={`block w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-slatex-100 ${currentLang === 'FR' ? 'text-brand-700' : 'text-slatex-600'}`}
              onClick={() => changeLanguage('fr')}
            >
              Français
            </button>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={open}
        className={mobile
          ? 'mt-2 btn-primary w-full'
          : 'btn-primary ml-2 !px-5 !py-2.5'}
      >
        {t('navbar.cta')}
      </button>
    </div>
  );
};

export default NavLinks;
